/**
 * Team Bios Edit Members Page
 *
 * Allows Owner/Manager to:
 * - View all team members
 * - Toggle member visibility on public page
 * - Override member bios (custom bio per studio)
 * - Manually reorder members via drag-and-drop or arrow buttons
 * - Upload custom photos for members
 * - View embed code
 */

import React, { useState, useMemo } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import {
  Box,
  Container,
  Typography,
  Paper,
  Alert,
  CircularProgress,
  Button,
  Card,
  CardContent,
  CardActions,
  Switch,
  FormControlLabel,
  TextField,
  IconButton,
  Avatar,
  Chip,
  Divider,
} from '@mui/material';
import {
  ArrowUpward as ArrowUpIcon,
  ArrowDownward as ArrowDownIcon,
  PhotoCamera as PhotoCameraIcon,
  Edit as EditIcon,
  Save as SaveIcon,
  Cancel as CancelIcon,
  ContentCopy as ContentCopyIcon,
  OpenInNew as OpenInNewIcon,
  DragIndicator as DragIndicatorIcon,
} from '@mui/icons-material';
import {
  DndContext,
  closestCenter,
  KeyboardSensor,
  PointerSensor,
  useSensor,
  useSensors,
  DragEndEvent,
} from '@dnd-kit/core';
import {
  arrayMove,
  SortableContext,
  sortableKeyboardCoordinates,
  useSortable,
  verticalListSortingStrategy,
} from '@dnd-kit/sortable';
import { CSS } from '@dnd-kit/utilities';
import Header from '../../components/layout/Header';
import {
  useGetTeamMemberBiosQuery,
  useUpdateTeamMemberBioMutation,
  useUploadTeamMemberPhotoMutation,
  useGetTeamBioConfigQuery,
} from '../../api/teamBiosApi';
import { useGetStudioQuery } from '../studios/studiosApi';
import type { TeamMemberBio } from '../../types/teamBios';

// Sortable Member Card Component
interface SortableMemberCardProps {
  member: TeamMemberBio;
  index: number;
  totalMembers: number;
  editingMemberId: string | null;
  editBio: string;
  editTitle: string;
  onEditClick: (member: TeamMemberBio) => void;
  onSaveBio: (member: TeamMemberBio) => void;
  onCancelEdit: () => void;
  onVisibilityToggle: (member: TeamMemberBio) => void;
  onPhotoUpload: (
    member: TeamMemberBio,
    event: React.ChangeEvent<HTMLInputElement>
  ) => void;
  onMoveUp: (member: TeamMemberBio, index: number) => void;
  onMoveDown: (member: TeamMemberBio, index: number) => void;
  setEditBio: (bio: string) => void;
  setEditTitle: (title: string) => void;
}

const SortableMemberCard: React.FC<SortableMemberCardProps> = ({
  member,
  index,
  totalMembers,
  editingMemberId,
  editBio,
  editTitle,
  onEditClick,
  onSaveBio,
  onCancelEdit,
  onVisibilityToggle,
  onPhotoUpload,
  onMoveUp,
  onMoveDown,
  setEditBio,
  setEditTitle,
}) => {
  const {
    attributes,
    listeners,
    setNodeRef,
    transform,
    transition,
    isDragging,
  } = useSortable({ id: member.id });

  const style = {
    transform: CSS.Transform.toString(transform),
    transition,
    opacity: isDragging ? 0.5 : 1,
  };

  return (
    <Card ref={setNodeRef} style={style} sx={{ position: 'relative' }}>
      <CardContent>
        <Box sx={{ display: 'flex', gap: 2, alignItems: 'flex-start' }}>
          {/* Drag Handle */}
          <Box
            // eslint-disable-next-line react/jsx-props-no-spreading
            {...attributes}
            // eslint-disable-next-line react/jsx-props-no-spreading
            {...listeners}
            sx={{
              cursor: isDragging ? 'grabbing' : 'grab',
              display: 'flex',
              alignItems: 'center',
              color: 'action.active',
              '&:hover': {
                color: 'primary.main',
              },
            }}
          >
            <DragIndicatorIcon />
          </Box>

          {/* Avatar */}
          <Box sx={{ position: 'relative' }}>
            <Avatar
              src={
                member.effective_photo_url ||
                member.user.avatar_url ||
                undefined
              }
              sx={{ width: 80, height: 80 }}
            >
              {member.user.first_name[0]}
              {member.user.last_name[0]}
            </Avatar>
            <label htmlFor={`photo-upload-${member.id}`}>
              <input
                accept="image/*"
                style={{ display: 'none' }}
                id={`photo-upload-${member.id}`}
                type="file"
                onChange={e => onPhotoUpload(member, e)}
              />
              <IconButton
                component="span"
                size="small"
                sx={{
                  position: 'absolute',
                  bottom: 0,
                  right: 0,
                  bgcolor: 'background.paper',
                }}
              >
                <PhotoCameraIcon fontSize="small" />
              </IconButton>
            </label>
          </Box>

          {/* Member Info */}
          <Box sx={{ flex: 1 }}>
            <Box
              sx={{
                display: 'flex',
                alignItems: 'center',
                gap: 1,
                mb: 1,
              }}
            >
              <Typography variant="h6">{member.user.full_name}</Typography>
              <Chip
                label={member.membership_role}
                size="small"
                color="primary"
              />
              {!member.is_using_profile_defaults && (
                <Chip
                  label="Custom"
                  size="small"
                  color="secondary"
                  variant="outlined"
                />
              )}
            </Box>

            {editingMemberId === member.id ? (
              // Edit Mode
              <Box
                sx={{
                  display: 'flex',
                  flexDirection: 'column',
                  gap: 2,
                }}
              >
                <TextField
                  label="Title"
                  value={editTitle}
                  onChange={e => setEditTitle(e.target.value)}
                  fullWidth
                  size="small"
                />
                <TextField
                  label="Bio"
                  value={editBio}
                  onChange={e => setEditBio(e.target.value)}
                  fullWidth
                  multiline
                  rows={4}
                  size="small"
                />
              </Box>
            ) : (
              // View Mode
              <>
                <Typography variant="body2" color="text.secondary" gutterBottom>
                  {member.effective_title}
                </Typography>
                <Typography variant="body2">
                  {member.effective_bio || 'No bio available'}
                </Typography>
              </>
            )}
          </Box>

          {/* Ordering Controls */}
          <Box
            sx={{
              display: 'flex',
              flexDirection: 'column',
              gap: 0.5,
            }}
          >
            <IconButton
              size="small"
              onClick={() => onMoveUp(member, index)}
              disabled={index === 0}
            >
              <ArrowUpIcon />
            </IconButton>
            <IconButton
              size="small"
              onClick={() => onMoveDown(member, index)}
              disabled={index === totalMembers - 1}
            >
              <ArrowDownIcon />
            </IconButton>
          </Box>
        </Box>
      </CardContent>

      <Divider />

      <CardActions sx={{ justifyContent: 'space-between' }}>
        <FormControlLabel
          control={
            <Switch
              checked={member.is_visible}
              onChange={() => onVisibilityToggle(member)}
            />
          }
          label="Visible on public page"
        />

        <Box>
          {editingMemberId === member.id ? (
            <>
              <Button
                startIcon={<SaveIcon />}
                onClick={() => onSaveBio(member)}
                sx={{ mr: 1 }}
              >
                Save
              </Button>
              <Button
                startIcon={<CancelIcon />}
                onClick={onCancelEdit}
                color="secondary"
              >
                Cancel
              </Button>
            </>
          ) : (
            <Button
              startIcon={<EditIcon />}
              onClick={() => onEditClick(member)}
            >
              Edit Bio
            </Button>
          )}
        </Box>
      </CardActions>
    </Card>
  );
};

const TeamBiosEditMembers: React.FC = () => {
  const { studioId } = useParams<{ studioId: string }>();
  const navigate = useNavigate();

  // Fetch team member bios
  const {
    data: membersData,
    isLoading: membersLoading,
    error: membersError,
  } = useGetTeamMemberBiosQuery(
    { studioId: studioId || '' },
    { skip: !studioId }
  );

  // Fetch config for embed code
  const { data: config } = useGetTeamBioConfigQuery(studioId || '', {
    skip: !studioId,
  });

  // Fetch studio data to get slug
  const { data: studio } = useGetStudioQuery(studioId || '', {
    skip: !studioId,
  });

  // Mutations
  const [updateMemberBio] = useUpdateTeamMemberBioMutation();
  const [uploadPhoto] = useUploadTeamMemberPhotoMutation();

  // Local state for editing
  const [editingMemberId, setEditingMemberId] = useState<string | null>(null);
  const [editBio, setEditBio] = useState('');
  const [editTitle, setEditTitle] = useState('');
  const [copySuccess, setCopySuccess] = useState(false);

  // Get members array with memoization
  const members = useMemo(
    () => membersData?.results || [],
    [membersData?.results]
  );

  // Local state for drag-and-drop
  const [localMembers, setLocalMembers] = useState<TeamMemberBio[]>([]);

  // Drag-and-drop sensors
  const sensors = useSensors(
    useSensor(PointerSensor),
    useSensor(KeyboardSensor, {
      coordinateGetter: sortableKeyboardCoordinates,
    })
  );

  // Sync members data to local state for drag-and-drop
  React.useEffect(() => {
    setLocalMembers(members);
  }, [members]);

  // Generate iframe embed code using studio slug
  const embedCode = useMemo(() => {
    if (!studio?.slug) return '';

    const productionUrl = 'https://tools.dance';
    const publicUrl = `${productionUrl}/public/team-bios/${studio.slug}`;

    return `<div style="max-height: 100%; overflow: auto;">
<iframe
  src="${publicUrl}"
  width="100%"
  height="100%"
  frameborder="0"
  title="${studio.name} Team Bios"
  style="border: none;"
></iframe></div>`;
  }, [studio?.slug, studio?.name]);

  // Handle visibility toggle
  const handleVisibilityToggle = async (member: TeamMemberBio) => {
    try {
      await updateMemberBio({
        studioId: studioId!,
        membershipId: member.membership,
        data: { is_visible: !member.is_visible },
      }).unwrap();
    } catch (err) {
      console.error('Failed to update visibility:', err);
    }
  };

  // Handle edit mode
  const handleEditClick = (member: TeamMemberBio) => {
    setEditingMemberId(member.id);
    setEditBio(member.custom_bio || member.effective_bio);
    setEditTitle(member.custom_title || member.effective_title);
  };

  // Handle save bio
  const handleSaveBio = async (member: TeamMemberBio) => {
    try {
      await updateMemberBio({
        studioId: studioId!,
        membershipId: member.membership,
        data: {
          custom_bio: editBio !== member.user.profile.bio ? editBio : null,
          custom_title:
            editTitle !== member.user.profile.title ? editTitle : null,
        },
      }).unwrap();
      setEditingMemberId(null);
    } catch (err) {
      console.error('Failed to save bio:', err);
    }
  };

  // Handle cancel edit
  const handleCancelEdit = () => {
    setEditingMemberId(null);
    setEditBio('');
    setEditTitle('');
  };

  // Handle photo upload
  const handlePhotoUpload = async (
    member: TeamMemberBio,
    event: React.ChangeEvent<HTMLInputElement>
  ) => {
    const file = event.target.files?.[0];
    if (!file) return;

    try {
      await uploadPhoto({
        studioId: studioId!,
        membershipId: member.membership,
        photo: file,
      }).unwrap();
    } catch (err) {
      console.error('Failed to upload photo:', err);
    }
  };

  // Handle ordering with arrows
  const handleMoveUp = async (member: TeamMemberBio, currentIndex: number) => {
    if (currentIndex === 0) return;

    const newIndex = currentIndex - 1;

    // Reorder the local array (swap with previous item)
    const reorderedMembers = arrayMove(localMembers, currentIndex, newIndex);
    setLocalMembers(reorderedMembers);

    // Update all members with new sequential display_order values
    try {
      const updates = reorderedMembers.map((m, index) =>
        updateMemberBio({
          studioId: studioId!,
          membershipId: m.membership,
          data: { display_order: index },
        }).unwrap()
      );

      await Promise.all(updates);
    } catch (err) {
      console.error('Failed to update order:', err);
      // Revert to original order on error
      setLocalMembers([...members]);
    }
  };

  const handleMoveDown = async (
    member: TeamMemberBio,
    currentIndex: number
  ) => {
    if (currentIndex === localMembers.length - 1) return;

    const newIndex = currentIndex + 1;

    // Reorder the local array (swap with next item)
    const reorderedMembers = arrayMove(localMembers, currentIndex, newIndex);
    setLocalMembers(reorderedMembers);

    // Update all members with new sequential display_order values
    try {
      const updates = reorderedMembers.map((m, index) =>
        updateMemberBio({
          studioId: studioId!,
          membershipId: m.membership,
          data: { display_order: index },
        }).unwrap()
      );

      await Promise.all(updates);
    } catch (err) {
      console.error('Failed to update order:', err);
      // Revert to original order on error
      setLocalMembers([...members]);
    }
  };

  // Handle copy embed code
  const handleCopyEmbedCode = async () => {
    if (!embedCode) return;

    try {
      await navigator.clipboard.writeText(embedCode);
      setCopySuccess(true);
      setTimeout(() => setCopySuccess(false), 2000);
    } catch (err) {
      console.error('Failed to copy embed code:', err);
    }
  };

  // Handle view public page
  const handleViewPublicPage = () => {
    if (!studio?.slug) return;
    // Use studio slug instead of UUID for SEO-friendly public URL
    window.open(`/public/team-bios/${studio.slug}`, '_blank');
  };

  // Handle drag end
  const handleDragEnd = async (event: DragEndEvent) => {
    const { active, over } = event;

    if (!over || active.id === over.id) {
      return;
    }

    const oldIndex = localMembers.findIndex(m => m.id === active.id);
    const newIndex = localMembers.findIndex(m => m.id === over.id);

    // Optimistically update the local state
    const reorderedMembers = arrayMove(localMembers, oldIndex, newIndex);
    setLocalMembers(reorderedMembers);

    // Update display_order values for all affected members
    try {
      const updates = reorderedMembers.map((member, index) =>
        updateMemberBio({
          studioId: studioId!,
          membershipId: member.membership,
          data: { display_order: index },
        }).unwrap()
      );

      await Promise.all(updates);
    } catch (err) {
      console.error('Failed to update member order:', err);
      // Revert to original order on error
      setLocalMembers([...members]);
    }
  };

  // Loading state
  if (membersLoading) {
    return (
      <>
        <Header />
        <Box
          display="flex"
          justifyContent="center"
          alignItems="center"
          minHeight="400px"
        >
          <CircularProgress />
        </Box>
      </>
    );
  }

  // Error state
  if (membersError || !membersData) {
    return (
      <>
        <Header />
        <Container maxWidth="lg" sx={{ mt: 4 }}>
          <Alert severity="error">
            Failed to load team members. Please try again later.
          </Alert>
        </Container>
      </>
    );
  }

  return (
    <>
      <Header />
      <Container maxWidth="lg" sx={{ mt: 4, mb: 4 }}>
        {/* Page Header */}
        <Box mb={4}>
          <Typography variant="h4" component="h1" gutterBottom>
            Edit Team Members
          </Typography>
          <Typography variant="body1" color="text.secondary">
            Manage member visibility, bios, photos, and display order (drag to
            reorder)
          </Typography>
        </Box>

        {/* Action Buttons */}
        <Box mb={3} sx={{ display: 'flex', gap: 2 }}>
          <Button variant="outlined" onClick={() => navigate('/team-bios')}>
            Back to Settings
          </Button>
          <Button
            variant="outlined"
            startIcon={<OpenInNewIcon />}
            onClick={handleViewPublicPage}
          >
            View Public Page
          </Button>
        </Box>

        {/* Embed Code Section */}
        {config && (
          <Paper sx={{ p: 3, mb: 3 }}>
            <Typography variant="h6" gutterBottom>
              Embed Code
            </Typography>
            <Box sx={{ position: 'relative' }}>
              <TextField
                fullWidth
                multiline
                rows={4}
                value={embedCode}
                InputProps={{ readOnly: true }}
                helperText={
                  embedCode
                    ? 'Copy this code to embed the team bios on your website'
                    : 'Loading embed code...'
                }
              />
              {embedCode && (
                <IconButton
                  sx={{
                    position: 'absolute',
                    top: 8,
                    right: 8,
                  }}
                  onClick={handleCopyEmbedCode}
                  color={copySuccess ? 'success' : 'default'}
                >
                  <ContentCopyIcon />
                </IconButton>
              )}
            </Box>
            {copySuccess && (
              <Alert severity="success" sx={{ mt: 2 }}>
                Embed code copied to clipboard!
              </Alert>
            )}
          </Paper>
        )}

        {/* Members List with Drag-and-Drop */}
        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
          {localMembers.length === 0 ? (
            <Alert severity="info">
              No team members found. Add members to your studio first.
            </Alert>
          ) : (
            <DndContext
              sensors={sensors}
              collisionDetection={closestCenter}
              onDragEnd={handleDragEnd}
            >
              <SortableContext
                items={localMembers.map(m => m.id)}
                strategy={verticalListSortingStrategy}
              >
                {localMembers.map((member, index) => (
                  <SortableMemberCard
                    key={member.id}
                    member={member}
                    index={index}
                    totalMembers={localMembers.length}
                    editingMemberId={editingMemberId}
                    editBio={editBio}
                    editTitle={editTitle}
                    onEditClick={handleEditClick}
                    onSaveBio={handleSaveBio}
                    onCancelEdit={handleCancelEdit}
                    onVisibilityToggle={handleVisibilityToggle}
                    onPhotoUpload={handlePhotoUpload}
                    onMoveUp={handleMoveUp}
                    onMoveDown={handleMoveDown}
                    setEditBio={setEditBio}
                    setEditTitle={setEditTitle}
                  />
                ))}
              </SortableContext>
            </DndContext>
          )}
        </Box>
      </Container>
    </>
  );
};

export default TeamBiosEditMembers;
